// src/app/app-error-boundary.tsx
// The outermost net. `RouteErrorElement` catches everything React Router owns, but the router
// cannot catch a throw from ABOVE itself — the query client, the auth store's hydrate path,
// `ActiveSessionConflictDialog`, or `RouterProvider`'s own first render. Those still
// white-screened after the router boundary landed, so this wraps `<RouterProvider>` in
// `App.tsx` and is the true last line.
//
// A CLASS component, deliberately: `getDerivedStateFromError` / `componentDidCatch` are the
// only React API for catching a render throw, and there is no hook equivalent.
//
// It cannot use router hooks (it sits above the provider), so its actions are document-level:
// reload, or navigate by assigning `location`. That is also the honest affordance — if the
// shell itself failed, an in-app soft recovery has nothing left to stand on.
import { Component, type ErrorInfo, type ReactNode } from 'react';
// Same reasoning as route-error-element.tsx: every event passes through the allowlist
// `beforeSend` in `observability/sentry.ts`, and no-ops entirely without a DSN.
import { captureException } from '@sentry/react';
import { formatErrorDetails, isChunkLoadError } from './error-classify';
import { AppErrorPage } from './error-page';
import { readHomePath } from '@/shared/lib/home-path';

interface Props {
  children: ReactNode;
}

interface State {
  error: unknown;
  /** Sentry event id, surfaced to the user as the support reference. */
  referenceId?: string;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: undefined };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // A stale chunk is an expected consequence of deploying under a live client, and
    // `observability/sentry.ts` already ignores it by message. Don't spend an event on it.
    const referenceId = isChunkLoadError(error) ? undefined : captureException(error);
    // The component stack is the only thing that localises a crash from above the router,
    // and it holds component names only — no props, no PHI.
    console.error('[AppErrorBoundary]', error, info.componentStack);
    this.setState({ referenceId });
  }

  render() {
    const { error, referenceId } = this.state;
    if (error === undefined) return this.props.children;

    const chunk = isChunkLoadError(error);
    return (
      <AppErrorPage
        // No `offline` branch here: a shell-level crash is not a connectivity state, and
        // `navigator.onLine` at this point would only mislabel a genuine crash.
        kind={chunk ? 'chunk' : 'crash'}
        variant="page"
        title={chunk ? undefined : "The app didn't start"}
        description={
          chunk
            ? undefined
            : 'Something went wrong before the app finished loading. Reloading usually fixes it — if it keeps happening, send the reference below to support.'
        }
        referenceId={referenceId}
        // Gated here, so a deployed build passes undefined and no stack can render.
        details={import.meta.env.DEV ? formatErrorDetails(error) : undefined}
        // Not `revalidate()` — there is no router in scope to revalidate, and the failure
        // was in the tree that would have to re-render. A document load is the only reset.
        onPrimary={() => window.location.reload()}
        secondaryLabel="Go to Today"
        onSecondary={() => {
          // Full document navigation, not `navigate()` — see the file header.
          window.location.assign(readHomePath());
        }}
      />
    );
  }
}
